import { ComponentPropsWithoutRef } from 'react';

import classNames from 'classnames';
import { format } from 'date-fns';

import { RatingItems } from '@uikit';

import { DeserializedAppData, DeserializedPublicUser } from '@api';

import KarmaPoint from '@uikit/icons/KarmaPoint';

import { REVIEW_TYPE, ReviewContentReviewData } from '@shared/api/types/review';
import NextLink from '@shared/common/components/NextLink';
import { formatNumberWithNSign } from '@shared/helpers/formatters';
import formatUsername from '@shared/helpers/formatUsername';
import { useFeatureToggles } from '@shared/hooks/useFeatureToggles';

import StoreAppCard from '@store:web/components/common/AppCard/StoreAppCard';
import Avatar from '@store:web/components/common/Avatar/Avatar';

import ReviewFeedback from '../ReviewFeedback/ReviewFeedback';
import ReviewFlag from '../ReviewFlag/ReviewFlag';
import TrustScoreTag from '../TrustScoreTag/TrustScoreTag';

export interface ReviewContentProps extends ComponentPropsWithoutRef<'section'> {
  showReadMore?: boolean;
  showReviewFlag?: boolean;
  isFeedbackHorizontal?: boolean;
  review: ReviewContentReviewData;
  isHideUser?: boolean;
  isShowApp?: boolean;
  readMoreUrl?: string;
  app: Pick<DeserializedAppData['attributes'], 'appLink' | 'appName'>;
  reviewTitleTag?: 'h2' | 'h3' | 'h4' | 'span';
}

const ReviewContent = ({
  review,
  showReadMore,
  showReviewFlag,
  reviewTitleTag: ReviewTitleTag = 'span',
  isHideUser,
  isShowApp,
  readMoreUrl,
  className,
  app,
  isFeedbackHorizontal,
  ...props
}: ReviewContentProps) => {
  const featuresToggles = useFeatureToggles();
  const {
    title,
    rating,
    timestamp,
    user = {} as DeserializedPublicUser,
    trustScore,
    app: appReview,
  } = review;

  const hasTrustScoreTag = typeof trustScore === 'string' || typeof trustScore === 'number';

  return (
    <div className={classNames('flex flex-col', className)} {...props}>
      <div className={classNames('flex flex-col gap-3 flex-grow')}>
        <div className="w-full grid grid-cols-[1fr_24px] gap-1">
          <div className="flex justify-between items-start max-sm:flex-col max-sm:gap-2 sm:items-center overflow-hidden">
            <ReviewTitleTag className="t-h4 truncate w-full" data-qa="review-title">
              {title}
            </ReviewTitleTag>

            {hasTrustScoreTag && (
              <TrustScoreTag score={trustScore} className="max-sm:-order-1 text-neutral-700" />
            )}
          </div>

          {showReviewFlag && <ReviewFlag review={review} app={app} />}
        </div>

        {rating && <RatingItems score={rating} />}

        <span className="t-body2 text-primary-700" data-qa="review-date">
          {timestamp ? format(new Date(timestamp), 'dd MMM yyyy') : 'Invalid date'}
        </span>

        {isShowApp && !!appReview && (
          <div className="bg-primary-50 rounded p-4">
            <StoreAppCard app={appReview} />
          </div>
        )}

        <p className={classNames('text-sm mb-auto break-words')}>
          <span className={showReadMore ? 'line-clamp-3' : ''} data-qa="review-text">
            {review.text}
          </span>
          {showReadMore && !readMoreUrl && (
            <span className="font-semibold cursor-pointer">Read More ››</span>
          )}
          {showReadMore && !!readMoreUrl && (
            <NextLink href={readMoreUrl} className="font-semibold text-secondary">
              Read More ››
            </NextLink>
          )}
        </p>

        {!isHideUser && (
          <Avatar
            userInfo={user.attributes}
            src={user.attributes?.icon}
            sizeInPX={40}
            onClick={(event) => event.stopPropagation()}
            containerClassName="mt-5"
            className="float-left mr-2.5"
            data-qa="review-creator-avatar"
          >
            <span className="block mb-1 truncate t-subtitle2" data-qa="review-creator-name">
              {formatUsername(user.attributes)}
            </span>

            <KarmaPoint className="icon-xs align-bottom mr-2 text-secondary-700" />

            <span
              className="t-caption2 inline-block align-middle truncate text-neutral-500"
              data-qa="review-creator-karma"
            >
              {formatNumberWithNSign(user.attributes?.karma)}
            </span>
          </Avatar>
        )}
      </div>

      {featuresToggles.reviewFeedback && review.type !== REVIEW_TYPE.SUGGESTION && (
        <>
          <div className="w-full h-px bg-primary-600/25 my-4" />
          <ReviewFeedback review={review} isHorizontal={isFeedbackHorizontal} />
        </>
      )}
    </div>
  );
};

export default ReviewContent;
